<template>
  <div
    class="sections-info"
    role="region"
    :aria-label="$t('common_words.section_configuration')"
    tabindex="0"
  >
    <article
      v-for="(section, index) in sections"
      :key="section.id || `${sectionName(section)}-${index}`"
      class="section-card"
    >
      <h6 class="section-card__title">
        {{ sectionName(section) }}
      </h6>

      <dl class="section-card__details">
        <template v-for="detail in sectionDetails(section)">
          <dt :key="`${detail.key}-label`">{{ detail.label }}</dt>
          <dd :key="`${detail.key}-value`">{{ detail.value }}</dd>
        </template>
      </dl>
    </article>
  </div>
</template>

<script>
export default {
  name: 'sections-info-dialog',
  props: {
    row: {
      type: Object,
      required: true,
    },
    column: {
      type: String,
      default: 'sections_rel',
    },
  },
  computed: {
    sections() {
      return Array.isArray(this.row[this.column])
        ? this.row[this.column].filter(
            (section) => section && !section.deleted_at
          )
        : [];
    },
  },
  methods: {
    firstValue(object, paths) {
      for (let index = 0; index < paths.length; index += 1) {
        const value = paths[index].split('.').reduce((result, property) => {
          return result && result[property] !== undefined
            ? result[property]
            : null;
        }, object);

        if (value !== null && value !== undefined && value !== '') {
          return value;
        }
      }

      return null;
    },

    displayValue(value) {
      return value !== null && value !== undefined && value !== ''
        ? value
        : this.$t('common_words.no_assignment');
    },

    sectionName(section) {
      return this.displayValue(
        this.firstValue(section, ['name', 'section.name'])
      );
    },

    blocks(section) {
      const blocks = Array.isArray(section.blocks) ? section.blocks : [];
      const names = blocks
        .filter((block) => block && !block.deleted_at)
        .map((block) => (typeof block === 'string' ? block : block.name))
        .filter(Boolean);

      return this.displayValue(names.join(', '));
    },

    relatedName(section, relation) {
      const aliases = {
        cropVariety: [
          'cropVariety.name',
          'crop_variety.name',
          'crop_variety_name',
          'variety.name',
        ],
        bed: ['bed.name', 'bed_name'],
        valve: ['valve.name', 'valve_name'],
      };

      return this.displayValue(this.firstValue(section, aliases[relation]));
    },

    area(section) {
      const value = this.displayValue(section.area);
      return section.area !== null &&
        section.area !== undefined &&
        section.area !== '' &&
        this.row.area_unit
        ? `${value} ${this.row.area_unit}`
        : value;
    },

    sectionDetails(section) {
      return [
        {
          key: 'blocks',
          label: this.$t('common_words.blocks'),
          value: this.blocks(section),
        },
        {
          key: 'variety',
          label: this.$t('common_words.variety'),
          value: this.relatedName(section, 'cropVariety'),
        },
        {
          key: 'bed',
          label: this.$t('common_words.bed'),
          value: this.relatedName(section, 'bed'),
        },
        {
          key: 'plants',
          label: this.$t('common_words.plants'),
          value: this.displayValue(section.plants),
        },
        {
          key: 'area',
          label: this.$t('common_words.area'),
          value: this.area(section),
        },
        {
          key: 'valve',
          label: this.$t('common_words.valve'),
          value: this.relatedName(section, 'valve'),
        },
      ];
    },
  },
};
</script>

<style scoped>
.sections-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  max-width: 100%;
  padding: 0.25rem;
}

.section-card {
  flex: 0 0 245px;
  padding: 0.75rem;
  border: 1px solid #6ba652;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-size: 0.9em;
}

.section-card__title {
  margin: 0 0 0.6rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ebeef5;
  color: #6ba652;
  font-weight: 600;
}

.section-card__details {
  display: grid;
  grid-template-columns: minmax(85px, auto) minmax(0, 1fr);
  gap: 0.45rem 0.6rem;
  margin: 0;
}

.section-card__details dt,
.section-card__details dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.section-card__details dt {
  font-weight: 600;
}
</style>
